import { useMemo, useState } from "react";

const actionLabels = {
  CREATE: "Angelegt",
  UPDATE: "Geaendert",
  DELETE: "Geloescht",
  LOGIN: "Login",
  LOGIN_FAILED: "Login fehlgeschlagen",
  LOGIN_BLOCKED: "Login gesperrt",
};

const entityLabels = {
  users: "Benutzer",
  suppliers: "Kunden",
  records: "Wareneingang/Retoure",
  extra_efforts: "Mehraufwand",
  backups: "Backups",
};

function formatJson(data) {
  if (!data) {
    return "-";
  }

  return JSON.stringify(data, null, 2);
}

function stringifyForSearch(value) {
  if (!value) {
    return "";
  }

  return JSON.stringify(value).toLowerCase();
}

function downloadCsv(fileName, rows) {
  const csvContent = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(";")
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function AuditLog({ logs }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterEntity, setFilterEntity] = useState("all");
  const [filterActor, setFilterActor] = useState("all");

  const actions = useMemo(() => {
    return Array.from(new Set(logs.map((log) => log.action).filter(Boolean)));
  }, [logs]);

  const entities = useMemo(() => {
    return Array.from(new Set(logs.map((log) => log.entity).filter(Boolean)));
  }, [logs]);

  const actors = useMemo(() => {
    return Array.from(
      new Set(
        logs
          .map((log) => log.actorUsername)
          .filter(Boolean)
      )
    );
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const actor = log.actorUsername || "";
      const query = searchTerm.trim().toLowerCase();
      const matchesAction = filterAction === "all" || log.action === filterAction;
      const matchesEntity = filterEntity === "all" || log.entity === filterEntity;
      const matchesActor = filterActor === "all" || actor === filterActor;
      const matchesSearch =
        query.length === 0 ||
        String(log.timestamp || "").toLowerCase().includes(query) ||
        String(log.actorUsername || "").toLowerCase().includes(query) ||
        String(log.entityId || "").toLowerCase().includes(query) ||
        stringifyForSearch(log.beforeData).includes(query) ||
        stringifyForSearch(log.afterData).includes(query);

      return matchesAction && matchesEntity && matchesActor && matchesSearch;
    });
  }, [filterAction, filterActor, filterEntity, logs, searchTerm]);

  const resetFilters = () => {
    setSearchTerm("");
    setFilterAction("all");
    setFilterEntity("all");
    setFilterActor("all");
  };

  const handleCsvDownload = () => {
    if (filteredLogs.length === 0) {
      window.alert("Es gibt keine Protokolleintraege fuer die aktuellen Filter.");
      return;
    }

    downloadCsv("audit-protokoll.csv", [
      [
        "Zeit",
        "Benutzer",
        "Aktion",
        "Bereich",
        "Datensatz",
        "Vorher",
        "Nachher",
        "Vorheriger Hash",
        "Hash",
      ],
      ...filteredLogs.map((log) => [
        log.timestamp,
        log.actorUsername || "System",
        actionLabels[log.action] || log.action,
        entityLabels[log.entity] || log.entity,
        log.entityId,
        formatJson(log.beforeData),
        formatJson(log.afterData),
        log.previousHash,
        log.entryHash,
      ]),
    ]);
  };

  return (
    <div className="page-stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1 className="page-title">Protokoll</h1>
        <p className="section-text">
          Alle relevanten Aktionen werden serverseitig mit Hash-Kette protokolliert.
        </p>
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Audit-Log</h2>
            <p className="section-text">
              Filtere nach Benutzer, Aktion, Bereich oder Textinhalt.
            </p>
          </div>
          <div className="inline-actions">
            <button onClick={handleCsvDownload} className="button button-primary">
              CSV herunterladen
            </button>
            <button onClick={resetFilters} className="button button-secondary">
              Filter zuruecksetzen
            </button>
          </div>
        </div>

        <div className="form-grid columns-3">
          <label className="field">
            <span>Suche</span>
            <input
              placeholder="Datensatz, Benutzer oder Inhalt suchen"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Aktion</span>
            <select
              value={filterAction}
              onChange={(event) => setFilterAction(event.target.value)}
            >
              <option value="all">Alle Aktionen</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {actionLabels[action] || action}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Bereich</span>
            <select
              value={filterEntity}
              onChange={(event) => setFilterEntity(event.target.value)}
            >
              <option value="all">Alle Bereiche</option>
              {entities.map((entity) => (
                <option key={entity} value={entity}>
                  {entityLabels[entity] || entity}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Benutzer</span>
            <select
              value={filterActor}
              onChange={(event) => setFilterActor(event.target.value)}
            >
              <option value="all">Alle Benutzer</option>
              {actors.map((actor) => (
                <option key={actor} value={actor}>
                  {actor}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="message message-info export-info">
          {filteredLogs.length === 0
            ? "Mit den aktuellen Filtern werden keine Protokolleintraege gefunden."
            : `Es werden aktuell ${filteredLogs.length} Protokolleintraege angezeigt.`}
        </div>

        {filteredLogs.length === 0 ? (
          <div className="empty-state">Keine Protokolleintraege vorhanden.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table audit-table">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Benutzer</th>
                  <th>Aktion</th>
                  <th>Bereich</th>
                  <th>Datensatz</th>
                  <th>Details</th>
                  <th>Hash</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.timestamp}</td>
                    <td>{log.actorUsername || "System"}</td>
                    <td>{actionLabels[log.action] || log.action}</td>
                    <td>{entityLabels[log.entity] || log.entity}</td>
                    <td>{log.entityId}</td>
                    <td>
                      <details className="audit-details">
                        <summary>Vorher/Nachher anzeigen</summary>
                        <div className="audit-detail-grid">
                          <div>
                            <strong>Vorher</strong>
                            <pre>{formatJson(log.beforeData)}</pre>
                          </div>
                          <div>
                            <strong>Nachher</strong>
                            <pre>{formatJson(log.afterData)}</pre>
                          </div>
                        </div>
                      </details>
                    </td>
                    <td>
                      <code>{String(log.entryHash || "").slice(0, 12)}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
